import { isEqual } from 'lodash';

import { FeatureFlag } from '@swf/core/enums';
import type { OpportunityStatus } from '@swf/core/models/sports/types/betSlip';
import { isAvailableOpportunity } from '@swf/core/models/sports/utils/betSlip/isOpportunity';
import type { TrackingContextValue } from '@swf/core/providers/TrackingContext';
import { getState, select } from '@swf/core/store';
import type { EffectParams } from '@swf/core/store';
import { buttonClick } from '@swf/generated/analytics/buttonClick';
import { sportsEvent, sportsMarket, sportsSelection } from '@swf/generated/analytics/entities';
import { RunnerStatus } from '@swf/sports/types/runner';
import type { RunnerId } from '@swf/sports/types/runner';
import { mergeWithExistingTrackingEntities } from '@swf/sports/utils/mergeWithTrackingContextValue';

const runnerStatusMap: Record<OpportunityStatus, RunnerStatus> = {
  AVAILABLE: RunnerStatus.ACTIVE,
  SUSPENDED: RunnerStatus.SUSPENDED,
  UNAVAILABLE: RunnerStatus.REMOVED
};

interface RemoveEffectProps {
  runnerId: RunnerId;
  trackingContext: TrackingContextValue;
}

const effects = {
  async removeSelectionWithTracking(
    { runnerId, trackingContext }: RemoveEffectProps,
    { dispatch, rootState }: EffectParams
  ) {
    const snowPlowEnabledInState = select.feature.check(rootState, FeatureFlag.SnowplowAnalytics);
    if (snowPlowEnabledInState === undefined) {
      await dispatch.feature.check({ features: [FeatureFlag.SnowplowAnalytics] });
    }
    const snowplowEnabled =
      snowPlowEnabledInState ?? select.feature.check(getState(), FeatureFlag.SnowplowAnalytics);

    const singleOpportunities = select.betSlip.singleOpportunities(rootState);
    const opportunity = singleOpportunities.find((opp) => isEqual(opp.runnerId, runnerId));
    if (snowplowEnabled && opportunity) {
      const trackingEntitites = mergeWithExistingTrackingEntities(trackingContext, [
        // eslint-disable-next-line @typescript-eslint/naming-convention
        sportsEvent({ sports_eventId: opportunity.eventId }),
        sportsMarket({ marketId: Number(runnerId.marketId) }),
        sportsSelection({
          selectionId: runnerId.selectionId,
          selectionOdds: isAvailableOpportunity(opportunity)
            ? opportunity.winRunnerOdds.decimalDisplayOdds?.decimalOdds ?? -1
            : 0,
          selectionStatus: runnerStatusMap[opportunity.status]
        })
      ]);
      buttonClick({ buttonName: 'Remove Selection' }, trackingEntitites);
    }

    dispatch.betSlip.selectionRemoved(runnerId);
  }
};

export default effects;
